import { useMutation } from '@tanstack/react-query';
import { api, upload } from '../client';
import type { Key } from '../../i18n';
import type { ProfileInput, Role, User } from '../types';

export interface AuthResult {
  user: User;
  pendingApproval?: boolean;
  message?: string;
}

export const authKeys = {
  me: ['auth', 'me'] as const,
};

export function fetchMe() {
  return api<{ user: User | null; suspended?: SuspendedInfo | null }>('/auth/me');
}

/**
 * `scope` picks which front door you are knocking on. The server refuses an
 * admin at the 'public' door and a non-admin at the 'admin' one, so a rejected
 * sign-in never gets a session cookie.
 */
export function useLogin() {
  return useMutation({
    mutationFn: (vars: { phone: string; password: string; scope?: 'public' | 'admin' }) =>
      api<AuthResult>('/auth/login', { method: 'POST', body: vars }),
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: () => api<{ ok: true }>('/auth/logout', { method: 'POST' }),
  });
}

/** Details a suspended account is shown instead of being let in. */
export interface SuspendedInfo {
  reason: string | null;
  suspendedAt?: number | null;
  /** False while an appeal is already open — only one at a time. */
  canAppeal?: boolean;
  appealPending?: boolean;
  appealSentAt?: number | null;
  /** Set when a previous appeal was turned down; cleared by appealing again. */
  declinedNote?: string | null;
  declinedAt?: number | null;
}

/**
 * Appeal a suspension. Not a logged-in action — a suspended account cannot sign
 * in — so it re-sends the credentials the login attempt just proved.
 */
export function useAppeal() {
  return useMutation({
    mutationFn: (vars: {
      phone: string;
      password: string;
      message: string;
      document?: { url: string; name: string };
    }) => api<{ ok: true; message: string }>('/auth/appeal', { method: 'POST', body: vars }),
  });
}

/** Attach a corrected licence or ID to an appeal, authorised by the same
 *  credentials the appeal itself carries. */
export function useUploadAppealDoc() {
  return useMutation({
    mutationFn: ({ file, phone, password }: { file: File; phone: string; password: string }) =>
      upload<{ url: string; name: string }>('/auth/appeal/document', 'document', [file], { phone, password }),
  });
}

/** Edit your own name / email / address, and optionally your password. */
export function useUpdateProfile() {
  return useMutation({
    mutationFn: (vars: ProfileInput) => api<{ user: User }>('/auth/me', { method: 'PUT', body: vars }),
  });
}

/**
 * Delete your own account. Refused (409) while you still have active/upcoming
 * bookings — cancel those first. On success the account is anonymised.
 */
export function useDeleteAccount() {
  return useMutation({
    mutationFn: (vars: { password: string }) =>
      api<{ ok: true }>('/auth/me', {
        method: 'DELETE',
        body: vars,
      }),
  });
}

/** Signup step 1: send a code to the phone. `otp` is returned while OTP_VIA_API
 *  is on (testing); once SMS delivery is verified the server stops returning it. */
export function useRequestOtp() {
  return useMutation({
    mutationFn: (vars: { phone: string }) =>
      api<{ ok: true; sent: boolean; expiresInSec: number; resendInSec?: number; otp?: string }>('/auth/otp/request', {
        method: 'POST',
        body: vars,
      }),
  });
}

/** Signup step 2: exchange the code for a short-lived signup token. */
export function useVerifyOtp() {
  return useMutation({
    mutationFn: (vars: { phone: string; code: string }) =>
      api<{ ok: true; verified: boolean; signupToken: string }>('/auth/otp/verify', {
        method: 'POST',
        body: vars,
      }),
  });
}

/** The `value` is what the server stores; `label` is a key, so the list reads
 *  in whichever language the person registering is using. */
export const ID_PROOF_TYPES = [
  { value: 'aadhaar', label: 'idAadhaar' },
  { value: 'pan', label: 'idPan' },
  { value: 'passport', label: 'idPassport' },
  { value: 'driving_licence', label: 'idDrivingLicence' },
  { value: 'voter_id', label: 'idVoterId' },
] as const satisfies ReadonlyArray<{ value: string; label: Key }>;

/**
 * How long each ID number is, and what may be typed into it.
 *
 * `digits` documents are numeric and fixed-length, so the box simply refuses a
 * thirteenth digit rather than accepting one and complaining later. The
 * alphanumeric ones are upper-cased as they are typed, which is how they are
 * printed on the document.
 *
 * A driving licence has no single national format — the states issue their own,
 * with and without spaces — so it gets a generous cap and no exact length. This
 * list is deliberately about length, not shape: rejecting a real ID because it
 * did not match a guessed pattern is far worse than accepting an odd-looking
 * one, which an admin reviews against the uploaded scan anyway.
 *
 * Mirrored by ID_PROOF_RULES in server.js — the client saves a round trip here,
 * it does not decide anything the server has not already decided.
 */
export const ID_PROOF_RULES: Record<string, { max: number; exact?: number; digits?: boolean }> = {
  aadhaar: { max: 12, exact: 12, digits: true },
  pan: { max: 10, exact: 10 },
  passport: { max: 8, exact: 8 },
  voter_id: { max: 10, exact: 10 },
  driving_licence: { max: 16 },
};

/**
 * What the ID number box keeps as it is typed: the allowed characters for the
 * chosen document, truncated to its length. A pasted Aadhaar keeps its digits
 * and loses the spaces it is usually printed with.
 */
export function sanitizeIdNumber(value: string, type: string): string {
  const rule = ID_PROOF_RULES[type] ?? { max: 32 };
  const kept = rule.digits ? value.replace(/\D/g, '') : value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return kept.slice(0, rule.max);
}

export interface IdProof {
  type: string;
  number: string;
  url: string;
  name: string;
}

/**
 * Upload a manager's ID proof mid-signup. There is no session yet, so the
 * signupToken from the OTP step rides along as a form field to authorise it.
 */
export function useUploadIdProof() {
  return useMutation({
    mutationFn: ({ file, signupToken }: { file: File; signupToken: string }) =>
      upload<{ url: string; name: string }>('/auth/signup/id-proof', 'document', [file], { signupToken }),
  });
}

/** Signup step 3: the phone comes from the token, not the body.
 *  `idProof` is required by the server when role is 'manager'. */
export function useSignup() {
  return useMutation({
    mutationFn: (vars: {
      signupToken: string;
      name: string;
      address: string;
      email: string;
      password: string;
      role: Role;
      idProof?: IdProof;
    }) => api<AuthResult>('/auth/signup', { method: 'POST', body: vars }),
  });
}
